// src/pages/Users/Galleries/GalleriesPhotos.jsx

import React, { useState } from "react";
import { FaImages } from "react-icons/fa";
import { ToastContainer } from "react-toastify";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Reveal from "./../../../components/Reveal/Reveal";

import useGalleriesController from "./hooks/useGalleriesController";

import GalleriesHero from "./components/GalleriesHero";
import AlbumsToolbar from "./components/AlbumsToolbar";
import GalleryCard from "./components/GalleryCard";
import AlbumPreviewModal from "./components/AlbumPreviewModal";
import SkeletonAlbumCard from "./components/SkeletonAlbumCard";
import EmptyState from "./components/EmptyState";
import CreateAlbumModal from "./CreateAlbumModal";

export default function GalleriesPhotos() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [previewAlbum, setPreviewAlbum] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const {
    loading,
    error,
    galleries,
    filtered,
    stats,
    ui,
    searchInput,
    setSearchInput,
    updateUi,
    resetFilters,
    refreshGalleries,
  } = useGalleriesController(isManager);

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Galeries photos"
          subtitle="Retrouve les souvenirs du club : compétitions, stages, remises de ceintures, événements et moments de vie."
          icon={FaImages}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <Reveal>
          <GalleriesHero
            totalAlbums={galleries.length}
            visibleAlbums={filtered.length}
            totalPhotos={stats.totalPhotos}
            inactiveAlbums={stats.inactiveAlbums}
            isManager={isManager}
          />
        </Reveal>

        <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
          <AlbumsToolbar
            ui={ui}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            isManager={isManager}
            onChange={updateUi}
            onReset={resetFilters}
            onCreate={() => setIsCreateModalOpen(true)}
            filteredCount={filtered.length}
          />
        </div>

        <section className="mt-5">
          {loading && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonAlbumCard key={index} />
              ))}
            </div>
          )}

          {!loading && error && (
            <EmptyState
              title="Galeries indisponibles"
              description={error}
              onReset={refreshGalleries}
              resetLabel="Réessayer"
            />
          )}

          {!loading && !error && filtered.length === 0 && (
            <EmptyState
              title="Aucun album trouvé"
              description="Aucun album ne correspond à ta recherche ou aux filtres sélectionnés."
              onReset={resetFilters}
            />
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((gallery, index) => (
                <GalleryCard
                  key={gallery.id}
                  gallery={gallery}
                  index={index}
                  userRole={userRole}
                  isManager={isManager}
                  onPreview={() => setPreviewAlbum(gallery)}
                  refreshGalleriesList={refreshGalleries}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <AlbumPreviewModal
        album={previewAlbum}
        isOpen={!!previewAlbum}
        onClose={() => setPreviewAlbum(null)}
      />

      {isCreateModalOpen && (
        <CreateAlbumModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            refreshGalleries();
          }}
          refreshList={refreshGalleries}
        />
      )}

      <ToastContainer />
    </section>
  );
}